import React, { useEffect, useRef, useState } from 'react';
import { Action } from './navigation-header-content';
import {
  Animated,
  Easing,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { DragHandle } from './drag-handle';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import { Props as NavigationHeaderProps } from './navigation-header';
import { PageContainer } from './page-container';
import {
  SCREEN_PADDING_LARGE,
  shadow,
  SPLIT_CONTAINER_MARGIN_HORIZONTAL,
} from '@/styles';

const LAYOUT_SIZE_TO_PRIMARY_CONTAINER_WIDTH_MAP: Record<LayoutSize, number> = {
  [LayoutSize.SMALL]: 0,
  [LayoutSize.SMALL_TABLET]: 360,
  [LayoutSize.MEDIUM]: 360,
  [LayoutSize.LARGE]: 420,
};

const PRIMARY_CONTAINER_MIN_WIDTH =
  LAYOUT_SIZE_TO_PRIMARY_CONTAINER_WIDTH_MAP[LayoutSize.MEDIUM];

export interface Props {
  actions?: Action[];
  baseStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
  enableResize?: boolean;
  isPrimaryPanelOpen?: boolean;
  navigationHeaderProps?: NavigationHeaderProps;
  primaryComponent: React.ReactNode;
  primaryMinWidth?: number;
  primaryPanelStyle?: StyleProp<ViewStyle>;
  secondaryMinWidth?: number;
  secondaryPanelStyle?: StyleProp<ViewStyle>;
  skipPanelSpacing?: boolean;
  title: React.ReactNode;
}

export const SplitPageContainer: React.FC<Props> = ({
  actions,
  baseStyle,
  children,
  enableResize = false,
  navigationHeaderProps = {},
  primaryComponent,
  primaryMinWidth,
  primaryPanelStyle,
  secondaryMinWidth,
  secondaryPanelStyle,
  skipPanelSpacing,
  title,
  isPrimaryPanelOpen = true,
}) => {
  const [styles] = useStyles();
  const defaultPrimaryContainerWidth = useLayoutSizeValue(
    LAYOUT_SIZE_TO_PRIMARY_CONTAINER_WIDTH_MAP,
  );

  const openValueRef = useRef(new Animated.Value(isPrimaryPanelOpen ? 1 : 0));

  const [containerWidth, setContainerWidth] = useState(0);
  const [resizedPrimaryWidth, setResizedPrimaryWidth] = useState<number | null>(
    null,
  );
  const [initialDragWidth, setInitialDragWidth] = useState<number | null>(null);
  const primaryContainerWidth =
    resizedPrimaryWidth ?? defaultPrimaryContainerWidth;

  const onContainerLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const getMaxPrimaryWidth = () => {
    const marginSpace = skipPanelSpacing
      ? 0
      : SPLIT_CONTAINER_MARGIN_HORIZONTAL;
    return Math.max(
      primaryMinWidth ?? PRIMARY_CONTAINER_MIN_WIDTH,
      containerWidth - (secondaryMinWidth ?? 0) - marginSpace,
    );
  };

  useEffect(() => {
    Animated.timing(openValueRef.current, {
      duration: 500,
      easing: Easing.out(Easing.exp),
      toValue: isPrimaryPanelOpen ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [isPrimaryPanelOpen]);

  return (
    <View onLayout={onContainerLayout} style={[styles.base, baseStyle]}>
      <Animated.View
        style={[
          styles.primaryContainer,
          skipPanelSpacing ? styles.primaryContainerFlush : null,
          primaryPanelStyle,
          {
            width: openValueRef.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, primaryContainerWidth],
            }),
          },
        ]}
        testID={`split-page-container-${isPrimaryPanelOpen ? 'open' : 'closed'}`}
      >
        <View
          style={[
            styles.primaryContainerInner,
            { width: primaryContainerWidth },
          ]}
        >
          <PageContainer
            navigationHeaderProps={{
              actions,
              containerStyle: styles.pageContainer,
              responsive: true,
              title,
              ...navigationHeaderProps,
            }}
          >
            {primaryComponent}
          </PageContainer>
        </View>
      </Animated.View>

      {Platform.OS === 'web' && enableResize && isPrimaryPanelOpen ? (
        <DragHandle
          onGestureEnd={() => {
            setInitialDragWidth(null);
          }}
          onGestureStart={() => {
            setInitialDragWidth(
              resizedPrimaryWidth ?? defaultPrimaryContainerWidth,
            );
          }}
          onGestureUpdate={(event) => {
            if (containerWidth <= 0 || initialDragWidth === null) {
              return;
            }

            const newWidth = Math.max(
              primaryMinWidth ?? PRIMARY_CONTAINER_MIN_WIDTH,
              Math.min(
                getMaxPrimaryWidth(),
                initialDragWidth + event.translationX,
              ),
            );

            setResizedPrimaryWidth(newWidth);
          }}
        />
      ) : null}

      <View style={[styles.secondaryContainer, secondaryPanelStyle]}>
        {children}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.background,
    flex: 1,
    flexDirection: 'row',
  },
  pageContainer: {
    paddingHorizontal: SPLIT_CONTAINER_MARGIN_HORIZONTAL,
  },
  primaryContainer: {
    backgroundColor: theme.colors.white,
    marginRight: SPLIT_CONTAINER_MARGIN_HORIZONTAL,
    overflow: 'hidden',
    zIndex: 10,
  },
  primaryContainerFlush: {
    marginRight: 0,
  },
  primaryContainerInner: {
    flex: 1,
  },
  secondaryContainer: {
    backgroundColor: theme.colors.white,
    flex: 2,
    overflow: 'hidden',
  },
}));

export const useSharedStyles = createStyles((theme) => ({
  listFooter: {
    height: 32,
    justifyContent: 'center',
    paddingBottom: 4,
  },
  listItemContainer: {
    ...shadow({
      elevation: 1,
      opacity: 0.15,
      radius: 8,
      x: 0,
      y: 2,
    }),
    borderColor: theme.colors.transparent,
    borderRadius: 4,
    borderWidth: 1,
    marginHorizontal: SPLIT_CONTAINER_MARGIN_HORIZONTAL,
  },
  listItemContainerSelected: {
    borderColor: theme.colors.accent,
  },
  loadingContainer: {
    marginTop: SCREEN_PADDING_LARGE,
  },
}));
